import React, { useState, useRef } from 'react';
import { Plus, Send, Mic, Square, X, Image, FileText, Video, Camera, Loader } from 'lucide-react';
import api from '../../api/client';
import toast from 'react-hot-toast';
import { addToQueue, fileToBase64, registerSync } from '../../utils/offlineQueue';

/*
  WhatsApp-style input bar. Usage:
  <ChatInput
    onSend={(text) => { ... }}         // called with text on send
    entityType="daily_update"          // for file uploads
    entityId="uuid"                    // for file uploads
    onFileUploaded={() => fetch()}     // refresh after upload
    placeholder="Type a message..."
    sendColor="brand"                  // brand | red | green
    offline={{ projectId, date }}      // if set, queues offline
  />
*/
export default function ChatInput({ onSend, entityType, entityId, onFileUploaded, placeholder = 'Type a message...', sendColor = 'brand', offline, onCreateForUpload }) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [showAttach, setShowAttach] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordDuration, setRecordDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const mediaRecorder = useRef(null);
  const chunks = useRef([]);
  const timerRef = useRef(null);

  const btnColor = sendColor === 'red' ? 'bg-red-500 hover:bg-red-600' : sendColor === 'green' ? 'bg-green-600 hover:bg-green-700' : 'bg-brand-600 hover:bg-brand-700';

  // ── Send text ──
  const handleSend = async () => {
    if (!text.trim()) return;
    setSending(true);
    if (offline && !navigator.onLine) {
      await addToQueue({ type: 'daily_update', projectId: offline.projectId, content_text: text, update_date: offline.date });
      registerSync();
      toast.success('Saved offline');
      setText('');
      setSending(false);
      return;
    }
    try {
      await onSend(text.trim());
      setText('');
    } catch {}
    setSending(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  // ── File upload ──
  const handleFiles = async (e, accept) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    setShowAttach(false);

    for (const file of files) {
      if (!navigator.onLine && entityType && entityId) {
        const base64 = await fileToBase64(file);
        await addToQueue({ type: 'media_upload', entity_type: entityType, entity_id: entityId, fileData: base64, fileName: file.name, mimeType: file.type });
        registerSync();
        toast.success(`${file.name} queued offline`);
        continue;
      }
      try {
        // For message contexts, create a stub message first to get an ID
        let uploadEntityId = entityId;
        if (onCreateForUpload) {
          try { uploadEntityId = await onCreateForUpload(`📎 ${file.name}`); } catch {}
        }
        const formData = new FormData();
        formData.append('file', file);
        if (entityType) formData.append('entity_type', entityType);
        if (uploadEntityId) formData.append('entity_id', uploadEntityId);
        await api.post('/media/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success(`${file.name} uploaded`);
      } catch {
        toast.error(`Failed: ${file.name}`);
      }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
    if (onFileUploaded) onFileUploaded();
  };

  // ── Voice recording ──
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4' });
      chunks.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.current.push(e.data); };
      recorder.onstop = () => {
        const blob = new Blob(chunks.current, { type: recorder.mimeType });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
      };
      recorder.start();
      mediaRecorder.current = recorder;
      setRecording(true);
      setRecordDuration(0);
      timerRef.current = setInterval(() => setRecordDuration(d => d + 1), 1000);
    } catch {
      toast.error('Microphone not available');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current?.state === 'recording') mediaRecorder.current.stop();
    setRecording(false);
    clearInterval(timerRef.current);
  };

  const sendVoice = async () => {
    if (!audioBlob) return;
    setUploading(true);
    try {
      let uploadEntityId = entityId;
      if (onCreateForUpload) {
        try { uploadEntityId = await onCreateForUpload('🎤 Voice note'); } catch {}
      }
      const formData = new FormData();
      formData.append('file', audioBlob, `voice_${Date.now()}.webm`);
      if (entityType) formData.append('entity_type', entityType);
      if (uploadEntityId) formData.append('entity_id', uploadEntityId);
      await api.post('/media/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Voice note sent');
      cancelVoice();
      if (onFileUploaded) onFileUploaded();
    } catch { toast.error('Upload failed'); }
    setUploading(false);
  };

  const cancelVoice = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordDuration(0);
  };

  const formatDuration = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  // ── Voice note recorded - show playback ──
  if (audioBlob) {
    return (
      <div className="flex items-center gap-2 bg-gray-100 rounded-full px-3 py-2">
        <button onClick={cancelVoice} className="p-1.5 text-red-500 hover:bg-red-100 rounded-full"><X size={18} /></button>
        <audio src={audioUrl} controls className="h-8 flex-1" />
        <span className="text-xs text-gray-500 font-mono">{formatDuration(recordDuration)}</span>
        <button onClick={sendVoice} disabled={uploading} className={`p-2.5 ${btnColor} text-white rounded-full disabled:opacity-50`}>
          {uploading ? <Loader size={18} className="animate-spin" /> : <Send size={18} />}
        </button>
      </div>
    );
  }

  // ── Recording in progress ──
  if (recording) {
    return (
      <div className="flex items-center gap-3 bg-red-50 rounded-full px-4 py-2.5 border border-red-200">
        <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
        <span className="text-sm text-red-600 font-mono flex-1">{formatDuration(recordDuration)}</span>
        <span className="text-xs text-red-400">Recording...</span>
        <button onClick={stopRecording} className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600">
          <Square size={16} />
        </button>
      </div>
    );
  }

  // ── Normal input bar ──
  return (
    <div className="relative">
      {/* Attachment menu */}
      {showAttach && (
        <div className="absolute bottom-full left-0 mb-2 bg-white rounded-2xl shadow-xl border p-4 grid grid-cols-4 gap-4 w-[280px] sm:w-72 z-30">
          <label className="flex flex-col items-center gap-1.5 cursor-pointer hover:bg-gray-50 rounded-xl p-2 active:bg-gray-100">
            <div className="w-12 h-12 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center"><Image size={22} className="text-blue-600" /></div>
            <span className="text-[11px] text-gray-600">Photo</span>
            <input type="file" accept="image/*" multiple className="hidden" onChange={e => handleFiles(e)} />
          </label>
          <label className="flex flex-col items-center gap-1.5 cursor-pointer hover:bg-gray-50 rounded-xl p-2 active:bg-gray-100">
            <div className="w-12 h-12 sm:w-10 sm:h-10 bg-purple-100 rounded-full flex items-center justify-center"><Video size={22} className="text-purple-600" /></div>
            <span className="text-[11px] text-gray-600">Video</span>
            <input type="file" accept="video/*" multiple className="hidden" onChange={e => handleFiles(e)} />
          </label>
          <label className="flex flex-col items-center gap-1.5 cursor-pointer hover:bg-gray-50 rounded-xl p-2 active:bg-gray-100">
            <div className="w-12 h-12 sm:w-10 sm:h-10 bg-green-100 rounded-full flex items-center justify-center"><FileText size={22} className="text-green-600" /></div>
            <span className="text-[11px] text-gray-600">File</span>
            <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx" multiple className="hidden" onChange={e => handleFiles(e)} />
          </label>
          <label className="flex flex-col items-center gap-1.5 cursor-pointer hover:bg-gray-50 rounded-xl p-2 active:bg-gray-100">
            <div className="w-12 h-12 sm:w-10 sm:h-10 bg-amber-100 rounded-full flex items-center justify-center"><Camera size={22} className="text-amber-600" /></div>
            <span className="text-[11px] text-gray-600">Camera</span>
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={e => handleFiles(e)} />
          </label>
        </div>
      )}

      <div className="flex items-center gap-2">
        {/* + Button */}
        <button onClick={() => setShowAttach(!showAttach)} className={`p-3 sm:p-2.5 rounded-full transition shrink-0 ${showAttach ? 'bg-gray-200 rotate-45' : 'bg-gray-100 hover:bg-gray-200 active:bg-gray-300'}`}>
          <Plus size={22} className="text-gray-600" />
        </button>

        {/* Input field */}
        <div className="flex-1 flex items-center bg-white border border-gray-300 rounded-full px-4 py-2.5 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-100">
          <input
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="flex-1 text-sm outline-none bg-transparent"
          />
        </div>

        {/* Send or Mic button */}
        {text.trim() ? (
          <button onClick={handleSend} disabled={sending} className={`p-3 sm:p-2.5 ${btnColor} text-white rounded-full disabled:opacity-50 transition shrink-0 active:scale-95`}>
            {sending ? <Loader size={20} className="animate-spin" /> : <Send size={20} />}
          </button>
        ) : (
          <button onClick={startRecording} className="p-3 sm:p-2.5 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-full transition shrink-0">
            <Mic size={22} className="text-gray-600" />
          </button>
        )}
      </div>

      {uploading && (
        <div className="absolute inset-0 bg-white/80 rounded-full flex items-center justify-center">
          <Loader size={20} className="animate-spin text-brand-600" />
          <span className="text-xs text-gray-500 ml-2">Uploading...</span>
        </div>
      )}
    </div>
  );
}
